import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import { object, string, ValidationError } from "yup";
import {
  Box,
  Button,
  Drawer,
  IconButton,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import {
  DataGrid,
  GridActionsCellItem,
  gridClasses,
  type GridColDef,
} from "@mui/x-data-grid";
import { useAtomValue, useSetAtom } from "jotai";
import { companyDataAtom } from "../test-run/test-run-atoms";
import { removeItemFromArray, trackSentryException } from "../../utils/helpers";
import { customAlertStateAtom } from "../../components/custom-alert";
import { Delete, Pause, PlayArrow } from "@mui/icons-material";
import { AsyncConfirmationModal } from "../../components/asyncConfirmationModal";
import { deleteAudio, deleteTemplate } from "../../utils/firebase_operations";
import { type AudioFile, type TemplateWithLabel } from "../../utils/types";
import { AuthContext } from "../../auth/AuthContext";
import { PlainCard } from "../../components/settings_components";
import { Howl } from "howler";
import UploadAudioFile from "./audios/upload-audio";

function AudiosContent() {
  const queryClient = useQueryClient();
  const { user } = useContext<any>(AuthContext);

  const { data: companyData, isLoading } = useAtomValue(
    companyDataAtom(user.companyId)
  );

  const [deleteTemplateDialogState, setDeleteTemplateDialogState] =
    useState<AudioFile | null>(null);

  const resetDeleteTemplateDialogState = useCallback(() => {
    setDeleteTemplateDialogState(null);
  }, []);

  const setCustomAlertState = useSetAtom(customAlertStateAtom);

  const deleteAudioMutation = useMutation({
    mutationKey: ["deleteAudio"],
    mutationFn: async () => {
      try {
        await deleteAudio({
          companyName: user.companyName,
          organisationId: user.companyId,
          fileName: deleteTemplateDialogState!.name,
        });

        await queryClient.setQueryData(
          ["getCompanyData", companyData!.organisationId],
          (oldData: any) => {
            const oldAudios: AudioFile[] = oldData?.audios ?? [];
            const removedAudioIndex = oldAudios.findIndex(
              (template) => template.name === deleteTemplateDialogState?.name
            );

            const newTemplates = removeItemFromArray(
              oldAudios,
              removedAudioIndex
            );

            return {
              ...oldData,
              audios: newTemplates,
            };
          }
        );

        setCustomAlertState({
          open: true,
          type: "success",
          title: `Audio file deleted successfully`,
        });
      } catch (error: any) {
        trackSentryException(error);
        console.log("Error occurred during deleting audio file", error);
        setCustomAlertState({
          open: true,
          type: "error",
          title: "Something went wrong",
          description: `An error occurred during deleting audio file`,
        });
      }
      resetDeleteTemplateDialogState();
    },
  });

  const [sounds, setSounds] = useState({});
  const [currentPlaying, setCurrentPlaying] = useState(null);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState({});
  const progressIntervals = useRef({});

  const createSound = useCallback(
    (audioId, url) => {
      if (sounds[audioId]) return sounds[audioId];

      setLoading((prev) => ({ ...prev, [audioId]: true }));

      const sound = new Howl({
        src: [url],
        html5: true,
        preload: false,
        onload: () => {
          setLoading((prev) => ({ ...prev, [audioId]: false }));
        },
        onloaderror: (id, error) => {
          console.error(`Error loading audio ${audioId}:`, error);
          setLoading((prev) => ({ ...prev, [audioId]: false }));
        },
        onplay: () => {
          setCurrentPlaying(audioId);

          // Start progress tracking
          const trackProgress = () => {
            if (sound.playing()) {
              const seek = sound.seek() || 0;
              const duration = sound.duration() || 0;
              setProgress((prev) => ({
                ...prev,
                [audioId]: duration > 0 ? (seek / duration) * 100 : 0,
              }));
            }
          };

          progressIntervals.current[audioId] = setInterval(trackProgress, 100);
        },
        onpause: () => {
          setCurrentPlaying(null);
          if (progressIntervals.current[audioId]) {
            clearInterval(progressIntervals.current[audioId]);
            delete progressIntervals.current[audioId];
          }
        },
        onend: () => {
          setCurrentPlaying(null);
          setProgress((prev) => ({ ...prev, [audioId]: 0 }));
          if (progressIntervals.current[audioId]) {
            clearInterval(progressIntervals.current[audioId]);
            delete progressIntervals.current[audioId];
          }
        },
      });

      setSounds((prev) => ({ ...prev, [audioId]: sound }));
      return sound;
    },
    [sounds]
  );

  const handlePlayPause = (audioId, url) => {
    const sound = createSound(audioId, url);

    if (currentPlaying === audioId) {
      sound.pause();
    } else {
      // Pause all other sounds
      Object.entries(sounds).forEach(([id, s]) => {
        if (parseInt(id) !== audioId && s.playing()) {
          s.pause();
        }
      });

      sound.play();
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      Object.values(sounds).forEach((sound) => {
        sound.unload();
      });
      Object.values(progressIntervals.current).forEach((interval) => {
        clearInterval(interval);
      });
    };
  }, [sounds]);

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 128,
    },
    {
      field: "label",
      headerName: "Label",
      flex: 1,
      minWidth: 128,
    },
    {
      field: "actions",
      type: "actions",
      minWidth: 128,
      headerName: "Actions",
      width: 100,
      cellClassName: "actions",
      renderCell: (params) => {
        console.log(params.row);
        const isPlaying = currentPlaying === params.row.name;
        const isLoading = loading[params.row.name];
        const currentProgress = progress[params.row.name] || 0;

        return (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Box sx={{ position: "relative" }}>
              <IconButton
                // onClick={() => handlePlayPause(params.row.id, params.row.url)}
                onClick={() => handlePlayPause(params.row.name, params.row.url)}
                color="primary"
                size="small"
                disabled={isLoading}
              >
                {isPlaying ? <Pause /> : <PlayArrow />}
              </IconButton>

              {currentProgress > 0 && (
                <LinearProgress
                  variant="determinate"
                  value={currentProgress}
                  sx={{
                    position: "absolute",
                    bottom: 2,
                    left: 4,
                    right: 4,
                    height: 2,
                    borderRadius: 1,
                  }}
                />
              )}
            </Box>
            <IconButton
              onClick={() => {
                setDeleteTemplateDialogState(params.row);
              }}
              color="error"
              size="small"
              sx={{
                "&:hover": {
                  backgroundColor: "error.light",
                  color: "white",
                },
              }}
            >
              <Delete />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  return (
    <>
      <div className="relative overflow-x-auto h-full">
        <AsyncConfirmationModal
          title={"Are you sure you want to delete the audio file?\n"}
          description={`File: ${deleteTemplateDialogState?.name ?? ""}`}
          actionTitle={"Delete"}
          backTitle={"Cancel"}
          closeHandler={resetDeleteTemplateDialogState}
          open={deleteTemplateDialogState != null}
          actionHandler={deleteAudioMutation.mutateAsync}
          actionIcon={<Delete />}
          submitting={deleteAudioMutation.isPending}
        />
        <DataGrid
          rows={companyData?.audios ?? []}
          loading={isLoading}
          showCellVerticalBorder={false}
          showColumnVerticalBorder={false}
          disableColumnMenu={true}
          className={"bg-white"}
          columns={columns}
          getRowId={(row) => row.name}
          isRowSelectable={(row) => false}
          initialState={{
            sorting: {
              sortModel: [{ field: "key", sort: "asc" }],
            },
          }}
          sx={{
            [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
              outline: "transparent",
            },
            [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
              {
                outline: "none",
              },
          }}
        />
      </div>
    </>
  );
}

function AudiosPanel() {
  return (
    <PlainCard>
      <div className={"flex h-full"}>
        <div className={"flex-[4_1_0%]"}>
          <div className="grid grid-cols-2">
            <h1 className="text-3xl pl-4 pb-6 font-bold">Audios</h1>
          </div>
          <div className="pl-4 pr-4 overflow-auto h-[calc(100%-50.25px)]">
            <AudiosContent />
          </div>
        </div>
        <UploadAudioFile />
      </div>
    </PlainCard>
  );
}

export default AudiosPanel;
